// Auth-init classification for a fetchMyPermissions() result.
//
// JAVASCRIPT-REACT-W3: fetchMyPermissions is sequence-guarded, so an older
// permissions request that resolves AFTER a newer one already succeeded comes
// back tagged `{ superseded: true }`. A superseded result — even a 401/403 —
// must NEVER clear the session: the newer fetch is the authoritative one, and
// clearing here would log the user out immediately after a valid load
// (e.g. an older practice's refresh-delayed 401 landing after the current
// practice's 200). Only a terminal, non-superseded auth-invalid status clears.

const AUTH_INVALID_STATUSES = new Set([401, 403]);

/**
 * Classify a fetchMyPermissions() result for the auth-init consumer
 * (JWTContext.fetchPermissionsSoftly). This is the single decision point used
 * by the consumer, exported so the consumer-level behaviour is directly
 * testable (the superseded guard has a failing negative control).
 *
 * @param {{ superseded?: boolean, status?: number }|null|undefined} result
 * @returns {{ authInvalid: boolean, status: number, superseded: boolean, reason: (string|null) }}
 *   authInvalid=true only for a terminal, non-superseded 401/403. `reason`
 *   accurately names the terminal failure (permissions_unauthorized /
 *   permissions_forbidden) — NOT a generic refresh_invalid — and preserves the
 *   HTTP status for telemetry.
 */
export function classifyPermissionsResult(result) {
    const status = result?.status ?? 0;
    const superseded = Boolean(result?.superseded);

    // Superseded results never clear — no matter the status.
    if (superseded) {
        return { authInvalid: false, status, superseded: true, reason: null };
    }

    const authInvalid = AUTH_INVALID_STATUSES.has(status);
    // Terminal auth-invalid on the PERMISSIONS endpoint is reported with an
    // accurate, permissions-scoped reason (not refresh_invalid), preserving the
    // status so telemetry distinguishes a 401 from a 403.
    let reason = null;
    if (authInvalid) {
        reason = status === 403 ? 'permissions_forbidden' : 'permissions_unauthorized';
    }
    return { authInvalid, status, superseded: false, reason };
}

/**
 * @param {{ superseded?: boolean, status?: number }|null|undefined} result
 * @returns {boolean} true only when a NON-superseded, auth-invalid (401/403)
 *   permissions result should clear the session.
 */
export function permissionsResultRequiresSessionClear(result) {
    return classifyPermissionsResult(result).authInvalid;
}
